/**
 * The "Create protocol" export: the PDF a user builds from the issues touched
 * in a chosen period.
 *
 * Not to be confused with the dormant `pm_protocols` / `pm_protocol_rows` /
 * `pm_protocol_attendees` schema, which `PMIssue.protocol_row_id` points at.
 * That is the same domain concept stored server-side; nothing in the frontend
 * reads it yet. If it is ever built, the two should converge rather than sit
 * side by side under different names.
 */
import { addDays, format, startOfDay, startOfMonth, startOfWeek } from "date-fns";
import type { Language, Translations } from "@/lib/i18n/types";
import { fill } from "@/lib/i18n/fill";
import type {
  ProtocolData,
  PMIssue,
  PMIssueActivity,
  PMIssueComment,
  IssueStatus,
} from "@/domains/pm/types";
import { renderActivityLine } from "@/domains/pm/utils/activityLine";
import { splitQuoteRuns } from "@/domains/pm/utils/commentQuote";
import { getDisplayName } from "@/domains/pm/utils/commentAuthor";
import { getDisciplineAbbreviation } from "@/domains/pm/utils/disciplineName";
import { DEFAULT_PROTOCOL_LAYOUT, type ProtocolLayout } from "@/domains/pm/utils/protocolLayout";
import type {
  ProtocolAttachmentContent,
  ProtocolOriginal,
} from "@/domains/pm/utils/protocolAttachments";

export type ProtocolPreset = "today" | "this_week" | "this_month" | "custom";
/**
 * The parts of the document a user can switch off, in the order they print.
 *
 * The details panel is deliberately absent: it is the only carrier of the
 * period, the building and the truncated/omitted caveats, so a protocol that
 * hid it would be indistinguishable from a complete record of the period. It
 * always prints, and the step says so rather than leaving the gap unexplained.
 */
/**
 * What each row of the issue table carries.
 *
 * `number` and `title` are absent on purpose: a table of neither is not a
 * table, so they always print and the step offers no tick for them.
 */
export const PROTOCOL_COLUMNS = [
  "status",
  "assignees",
  "disciplines",
  "startDate",
  "dueDate",
  "commentCount",
] as const;

export type ProtocolColumnId = (typeof PROTOCOL_COLUMNS)[number];

export type ProtocolColumnSelection = Readonly<Record<ProtocolColumnId, boolean>>;

/**
 * Assignees and due date: who owes what, by when — what a protocol is read
 * for. Everything else is opted into: comments cost a fetch and lengthen the
 * document, and every extra column narrows the title (see
 * NARROW_COLUMN_WIDTHS), which is the one cell every row needs.
 */
export function protocolColumns(
  overrides: Partial<ProtocolColumnSelection> = {}
): ProtocolColumnSelection {
  return {
    status: false,
    assignees: true,
    disciplines: false,
    startDate: false,
    dueDate: true,
    commentCount: false,
    ...overrides,
  };
}

/** The picker's own view of the period. Never reaches the document. */
export interface ProtocolIssueFilter {
  statuses: ReadonlySet<IssueStatus>;
  disciplineIds: ReadonlySet<number>;
}

export const EMPTY_PROTOCOL_FILTER: ProtocolIssueFilter = {
  statuses: new Set(),
  disciplineIds: new Set(),
};

export function isProtocolFilterActive(filter: ProtocolIssueFilter): boolean {
  return filter.statuses.size > 0 || filter.disciplineIds.size > 0;
}

/**
 * Whether an issue survives the picker's filters.
 *
 * An empty set means "not filtering on this", not "match nothing" — the two
 * dimensions are ANDed, and an issue's several disciplines are ORed within
 * theirs, so picking two disciplines shows issues carrying either.
 */
export function matchesProtocolFilter(issue: PMIssue, filter: ProtocolIssueFilter): boolean {
  if (filter.statuses.size > 0 && !filter.statuses.has(issue.status)) return false;
  if (filter.disciplineIds.size === 0) return true;
  return (issue.disciplines ?? []).some((discipline) => filter.disciplineIds.has(discipline.id));
}

export const PROTOCOL_SECTIONS = ["summary", "issues", "activity"] as const;

export type ProtocolSectionId = (typeof PROTOCOL_SECTIONS)[number];

export type ProtocolSectionSelection = Readonly<Record<ProtocolSectionId, boolean>>;

/** Every section on — the wizard's default, and the shape a caller names a
 *  variation against rather than restating the whole record. */
export function protocolSections(
  overrides: Partial<ProtocolSectionSelection> = {}
): ProtocolSectionSelection {
  return { summary: true, issues: true, activity: true, ...overrides };
}

/**
 * One block the user wrote themselves. Printed after the summary and before the
 * issue table — a protocol's decisions and agreements belong above the record
 * they were taken against.
 */
export interface ProtocolNote {
  /** Unique only within one open wizard — a React key, not an id. */
  rowId: string;
  heading: string;
  body: string;
}

/** A row the user has touched. A wholly blank one is just an unused row. */
export function isStartedProtocolNote(note: ProtocolNote): boolean {
  return note.heading.trim() !== "" || note.body.trim() !== "";
}

/** A row worth printing: a heading names it, a body says something under it. */
export function isCompleteProtocolNote(note: ProtocolNote): boolean {
  return note.heading.trim() !== "" && note.body.trim() !== "";
}

/**
 * Rows the user started and did not finish. They block the export rather than
 * being dropped: a heading with nothing under it reads as content that was
 * lost, and silently discarding typed text is worse than saying why.
 */
export function incompleteProtocolNotes(notes: readonly ProtocolNote[]): ProtocolNote[] {
  return notes.filter((note) => isStartedProtocolNote(note) && !isCompleteProtocolNote(note));
}

/**
 * Whether the chosen contents amount to a document. The details panel alone is
 * not one: it says what the export covers, and covering nothing is not a
 * protocol.
 */
export function hasProtocolContent(
  sections: ProtocolSectionSelection,
  notes: readonly ProtocolNote[],
  attachmentCount = 0
): boolean {
  return (
    PROTOCOL_SECTIONS.some((id) => sections[id]) ||
    notes.some(isStartedProtocolNote) ||
    attachmentCount > 0
  );
}

let noteCounter = 0;

/** Row ids only have to be unique within one open wizard, and a counter keeps
 *  tests deterministic — no UUID dependency for what is a React key. */
export function createProtocolNote(): ProtocolNote {
  noteCounter += 1;
  return { rowId: `note-${noteCounter}`, heading: "", body: "" };
}

export interface ProtocolDocumentNote {
  heading: string;
  /** The body's non-blank lines, in order — see `noteParagraphs`. */
  paragraphs: string[];
}

/**
 * A typed body, split into the lines it will print as.
 *
 * `wrapText` already honours newlines — `toWinAnsi` keeps `\n` precisely so it
 * can — so this is not what rescues them. It drops the blank lines between
 * paragraphs, which would otherwise print as bands of nothing; replaces the
 * tabs `toWinAnsi` can only render as "?" with spaces; and trims each line so
 * the printed block matches what the textarea showed.
 *
 * Every non-blank line is its own paragraph, not just the ones after a blank
 * line: people type attendee and decision lists one per line, and joining those
 * into a run-on sentence is the one outcome that is plainly wrong.
 */
export function noteParagraphs(body: string): string[] {
  // Two plain splits rather than the `/\r\n|\r|\n/` alternation they replace:
  // Lizard (Codacy's complexity tool) mis-tokenises an escaped newline inside a
  // regex literal and reported this five-line function as 87 lines. The result
  // is the same — a CRLF leaves an empty piece between the two splits, which
  // the blank-line filter below already drops.
  return body
    .split("\n")
    .flatMap((line) => line.split("\r"))
    .map((line) => line.replace(/\t/g, " ").trim())
    .filter((line) => line.length > 0);
}

export interface ProtocolDateRange {
  from: Date;
  to: Date;
}

/**
 * The date the author puts on the protocol, printed in the letterhead's
 * top-right corner. It is theirs to set — the meeting's date, say — and says
 * nothing about the period or when the file was made; "Generated" in the
 * details panel records the latter whatever this holds.
 */
export interface ProtocolDocumentDate {
  /** `yyyy-MM-dd`, or null for a protocol that prints no date. */
  date: string | null;
  /** 24-hour `HH:mm`, or "" for a date printed without a time. */
  time: string;
}

/** Now, to the five minutes the time picker offers — so the prefilled time
 *  is one the picker can show as selected. */
export function defaultProtocolDocumentDate(now: Date = new Date()): ProtocolDocumentDate {
  const minutes = now.getMinutes() - (now.getMinutes() % 5);
  return {
    date: format(now, "yyyy-MM-dd"),
    time: `${String(now.getHours()).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`,
  };
}

/**
 * The letterhead's date line, in the document's language and the same format
 * as its other timestamps. Null when there is no date: a time on its own names
 * no moment, so it is never printed alone.
 */
function isRecordedDay(local: Date, year: number, month: number, date: number): boolean {
  return local.getFullYear() === year && local.getMonth() + 1 === month && local.getDate() === date;
}

export function formatProtocolDocumentDate(
  value: ProtocolDocumentDate | null | undefined,
  language: Language
): string | null {
  const day = value?.date ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.date) : null;
  if (!value || !day) return null;
  const time = /^(\d{2}):(\d{2})$/.exec(value.time);
  const [, year, month, date] = day;
  const local = new Date(0);
  local.setFullYear(Number(year), Number(month) - 1, Number(date));
  local.setHours(time ? Number(time[1]) : 0, time ? Number(time[2]) : 0, 0, 0);
  if (!isRecordedDay(local, Number(year), Number(month), Number(date))) return null;
  return dateFormatter(language, Boolean(time)).format(local);
}

export interface ProtocolDocumentActivity {
  timestamp: string;
  text: string;
}

/** One comment as the document prints it — resolved, never a raw row. */
export interface ProtocolDocumentComment {
  author: string;
  timestamp: string;
  /** A reply's quoted run, `> ` markers stripped. Empty when not a reply. */
  quote: string[];
  /** The author's own words. A single "(attachment only)" line when the
   *  comment carried no text of its own. */
  body: string[];
}

export interface ProtocolDocumentIssue {
  id: number;
  number: string;
  title: string;
  status: string;
  assignees: string;
  disciplines: string;
  startDate: string;
  dueDate: string;
  /** The count column's cell — a string like every other, so the renderer
   *  never formats. A dash when the comment scan was cut short on this issue:
   *  the count is unknown, and "0" would be a wrong number stated as fact. */
  commentCount: string;
  comments: ProtocolDocumentComment[];
  activity: ProtocolDocumentActivity[];
  /** The attachment numbers this row's thumbnail cell shows. */
  attachments: number[];
}

/** One attached file as the document prints it, numbered in print order. */
export interface ProtocolDocumentAttachmentFile {
  number: number;
  filename: string;
  content: ProtocolAttachmentContent;
  /** The file itself, embedded behind a paperclip. Absent when not wanted or
   *  left out to keep the PDF's size in bounds. */
  original?: ProtocolOriginal | null;
}

/** Every attached file once, and where each prints. */
export interface ProtocolDocumentAttachments {
  files: ProtocolDocumentAttachmentFile[];
  /** The appendix, in order. A null heading is the author's own attachments;
   *  `issueId` is what the group's heading links back to in the table. */
  groups: Array<{ issueId?: number | null; heading: string | null; numbers: number[] }>;
  /** Per issue id, the files its table row shows. Null for no table column. */
  table: ReadonlyMap<number, number[]> | null;
}

export interface ProtocolDocumentModel {
  language: Language;
  /** The look — never the content. See `ProtocolLayout`. */
  layout: ProtocolLayout;
  title: string;
  /** The author's date for the letterhead, already formatted — see
   *  `ProtocolDocumentDate`. Null prints nothing there. */
  date: string | null;
  period: string;
  building: string;
  project: string;
  /** The included section names, joined — see `documentContents`. */
  contents: string;
  generated: string;
  /**
   * Set only when the picker dropped issues, so the header can say how much of
   * the period this document covers. Null for a complete export — a line
   * reading "12 of 12" on every protocol would train the reader to skip it.
   */
  selection: string | null;
  /**
   * Which sections this document prints. The renderer guards on these rather
   * than on empty arrays: an empty `issues` already means "the period held
   * none", and a document whose author excluded the table must not read the
   * same as one whose period was empty.
   */
  include: ProtocolSectionSelection;
  /** Which columns the issue table carries. Read by `tableColumns`. */
  columns: ProtocolColumnSelection;
  /** Already normalised and split — the renderer never touches raw text. */
  notes: ProtocolDocumentNote[];
  /** Null when nothing is attached, so the section is absent rather than empty. */
  attachments: ProtocolDocumentAttachments | null;
  /** The issue table ends in a thumbnail column of each issue's files. */
  attachmentsInTable: boolean;
  truncated: boolean;
  /** The comment scan hit its cap — a different caveat from `truncated`,
   *  which is about activity, so it gets its own line rather than muddying
   *  a sentence the reader already knows the meaning of. */
  commentsTruncated: boolean;
  omitted: boolean;
  summary: {
    inProgress: number;
    resolved: number;
    closed: number;
  };
  issues: ProtocolDocumentIssue[];
  labels: Translations["pm"]["issues"]["protocol"]["document"];
}

interface BuildProtocolDocumentInput {
  data: ProtocolData;
  range: ProtocolDateRange;
  sections: ProtocolSectionSelection;
  columns: ProtocolColumnSelection;
  /** Half-filled rows never reach here — the wizard blocks Export on them. */
  notes?: readonly ProtocolNote[];
  /** Null while the building detail is still loading — printed as "Unknown". */
  buildingName: string | null | undefined;
  /**
   * The scoped project's id, independent of whether its name has resolved. It
   * is what separates "the export covers the whole building" from "the export
   * is scoped to a project we cannot name yet".
   */
  buildingProjectId?: number | null;
  projectName?: string | null;
  /**
   * How many issues the period actually holds, before the picker filtered
   * them. `data` arrives already filtered, so the count cannot be recovered
   * from it — omit it and the document simply claims nothing about selection.
   */
  availableIssueCount?: number;
  language: Language;
  translations: Translations;
  generatedAt?: Date;
  /** The author's date for the letterhead. Omitted, the letterhead prints none. */
  documentDate?: ProtocolDocumentDate | null;
  /** Omitted, the classic layout. */
  layout?: ProtocolLayout;
  /** Files with their bytes already loaded — see `protocolAttachmentLoader`. */
  attachments?: ProtocolDocumentAttachments | null;
  /** Ordered status keys from the catalogue. Without it the built-in three. */
  statusOrder?: string[];
  /** Whether an issue counts as live work, from the catalogue's categories. */
  isOpenStatus?: (issue: PMIssue) => boolean;
}

/** What a cell shows when the issue has no value for it. */
const EMPTY_VALUE = "-";

/** Nothing was cut short — hoisted so the map below allocates no Set per issue. */
const EMPTY_ISSUE_IDS: ReadonlySet<number> = new Set<number>();

const BUILT_IN_STATUS_ORDER: string[] = ["in_progress", "resolved", "closed"];

/**
 * The order the document prints issues in: the built-ins in workflow order,
 * then a project's own statuses, each by issue number. Exported so the picker
 * lists them the same way — a row order that disagrees with the PDF makes the
 * preview look like it reordered the selection.
 *
 * `statusOrder` comes from the status catalogue. A status missing from it
 * sorts last rather than first, which is what a bare index lookup would do
 * with its -1.
 */
export function sortProtocolIssues(
  issues: PMIssue[],
  statusOrder: string[] = BUILT_IN_STATUS_ORDER
): PMIssue[] {
  const rank = (status: string) => {
    const i = statusOrder.indexOf(status);
    return i === -1 ? Number.MAX_SAFE_INTEGER : i;
  };
  return [...issues].sort((a, b) => {
    const statusDifference = rank(a.status) - rank(b.status);
    return statusDifference || a.issue_number - b.issue_number;
  });
}

function orderedRange(range: ProtocolDateRange): ProtocolDateRange {
  return range.from <= range.to ? range : { from: range.to, to: range.from };
}

export function protocolRangeForPreset(
  preset: Exclude<ProtocolPreset, "custom">,
  now = new Date()
): ProtocolDateRange {
  const to = startOfDay(now);
  if (preset === "this_week") {
    return { from: startOfWeek(to, { weekStartsOn: 1 }), to };
  }
  if (preset === "this_month") {
    return { from: startOfMonth(to), to };
  }
  return { from: to, to };
}

export function protocolQueryBounds(range: ProtocolDateRange): {
  since: string;
  until: string;
} {
  const ordered = orderedRange(range);
  return {
    since: startOfDay(ordered.from).toISOString(),
    until: addDays(startOfDay(ordered.to), 1).toISOString(),
  };
}

/**
 * Identity of a fetched period: the resolved query bounds plus the project
 * scope. Keyed on the BOUNDS rather than the preset, so "today" and a custom
 * range covering only today are one cache entry rather than two.
 */
export function protocolDataKey(
  buildingId: number,
  range: ProtocolDateRange,
  buildingProjectId?: number | null
): string {
  const { since, until } = protocolQueryBounds(range);
  return `${buildingId}|${since}|${until}|${buildingProjectId ?? "all"}`;
}

/**
 * Narrow a fetched period to the issues the user ticked.
 *
 * BOTH halves are filtered. The issue table, the summary counts and the
 * activity section are all derived from this one object, so filtering only
 * `issues` would print activity under an issue the document never lists.
 *
 * `truncated` and `omitted` carry through untouched: both describe the FETCH,
 * not the selection, and both stay true after it. The period really did hold
 * more activity than one export pulls, and the changes whose issue could not
 * be read were never selectable in the first place — dropping either flag
 * would let a partial record read as a complete one.
 */
export function filterProtocolData(
  data: ProtocolData,
  includedIssueIds: ReadonlySet<number>
): ProtocolData {
  return {
    issues: data.issues.filter((issue) => includedIssueIds.has(issue.id)),
    activity: data.activity.filter((row) => includedIssueIds.has(row.issue_id)),
    // Undefined survives as undefined: narrowing "never fetched" into an empty
    // map would make the document print a comments section claiming the
    // selected issues have none.
    comments: data.comments
      ? new Map(Array.from(data.comments).filter(([issueId]) => includedIssueIds.has(issueId)))
      : undefined,
    commentsTruncated: data.commentsTruncated,
    // Narrowed like the comments themselves: an unknown count on an issue the
    // document never lists is not this document's problem.
    commentsIncomplete: data.commentsIncomplete
      ? new Set(Array.from(data.commentsIncomplete).filter((id) => includedIssueIds.has(id)))
      : undefined,
    truncated: data.truncated,
    omitted: data.omitted,
  };
}

export function protocolFilename(range: ProtocolDateRange): string {
  const ordered = orderedRange(range);
  const from = format(ordered.from, "yyyy-MM-dd");
  const to = format(ordered.to, "yyyy-MM-dd");
  const period = from === to ? from : `${from}-to-${to}`;
  return `protocol-${period}.pdf`;
}

function dateFormatter(language: Language, withTime = false): Intl.DateTimeFormat {
  return new Intl.DateTimeFormat(language === "et" ? "et-EE" : "en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    ...(withTime ? { hour: "2-digit", minute: "2-digit" } : {}),
  });
}

export function formatProtocolRange(range: ProtocolDateRange, language: Language): string {
  const ordered = orderedRange(range);
  const formatter = dateFormatter(language);
  const from = formatter.format(ordered.from);
  const to = formatter.format(ordered.to);
  return from === to ? from : `${from} – ${to}`;
}

function displayName(issue: PMIssue, fallback: string): string {
  const names = (issue.assignees ?? []).map((user) => {
    const fullName = [user.first_name, user.last_name].filter(Boolean).join(" ").trim();
    return fullName || user.email;
  });
  return names.length > 0 ? names.join(", ") : fallback;
}

/**
 * A `yyyy-MM-dd` due date in the active locale. Exported so the issue picker
 * prints the same date the PDF does rather than growing its own formatter.
 */
export function formatDateOnly(value: string, language: Language): string {
  const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!parts) return value;
  const [, year, month, day] = parts;
  const localDate = new Date(Number(year), Number(month) - 1, Number(day));
  return dateFormatter(language).format(localDate);
}

function activityByIssue(
  rows: PMIssueActivity[],
  translations: Translations,
  language: Language
): Map<number, ProtocolDocumentActivity[]> {
  const result = new Map<number, ProtocolDocumentActivity[]>();
  const formatter = dateFormatter(language, true);
  const ordered = [...rows].sort(
    (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime() || a.id - b.id
  );
  for (const row of ordered) {
    const items = result.get(row.issue_id) ?? [];
    items.push({
      timestamp: formatter.format(new Date(row.created_at)),
      text: renderActivityLine(row, translations, language),
    });
    result.set(row.issue_id, items);
  }
  return result;
}

/**
 * The period's comments, resolved into what the document prints.
 *
 * A reply's leading `> ` quote run is dropped — it repeats text already printed
 * above it, and in a filed record the same words twice reads as an error.
 * Attachment-only comments carry an empty body and are omitted rather than
 * printed as a blank line under someone's name.
 */
function commentsByIssue(
  rows: Map<number, PMIssueComment[]>,
  language: Language,
  copy: Translations["pm"]["issues"]["protocol"]["document"]
): Map<number, ProtocolDocumentComment[]> {
  const formatter = dateFormatter(language, true);
  const result = new Map<number, ProtocolDocumentComment[]>();
  for (const [issueId, comments] of rows) {
    const ordered = [...comments].sort(
      (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime() || a.id - b.id
    );
    result.set(
      issueId,
      ordered.map((comment) => {
        const runs = splitQuoteRuns(comment.body);
        const quoted = runs.find((run) => run.quoted)?.text ?? "";
        const own = runs
          .filter((run) => !run.quoted)
          .map((run) => run.text)
          .join("\n");
        const body = noteParagraphs(own);
        return {
          author: getDisplayName(comment),
          timestamp: formatter.format(new Date(comment.created_at)),
          // The quote is kept, not stripped: a reply reading "Agreed" means
          // nothing without what it agreed to.
          quote: noteParagraphs(quoted),
          // An attachment-only comment has no words, but that somebody
          // attached something on that date IS the record — so it prints.
          body: body.length > 0 ? body : [copy.commentEmpty],
        };
      })
    );
  }
  return result;
}

function buildDocumentIssue(
  issue: PMIssue,
  translations: Translations,
  activities: Map<number, ProtocolDocumentActivity[]>,
  comments: Map<number, ProtocolDocumentComment[]>,
  incompleteComments: ReadonlySet<number>,
  language: Language,
  attachmentTable: ReadonlyMap<number, number[]> | null
): ProtocolDocumentIssue {
  const issueComments = comments.get(issue.id) ?? [];
  return {
    id: issue.id,
    number: `#${issue.issue_number}`,
    title: issue.title,
    status: translations.pm.issues.status[issue.status],
    // A dense table reads absence better as a dash than as prose: "Vastutaja
    // puudub" in a cell scans as content, and three of them in a row bury the
    // values that are actually there.
    assignees: displayName(issue, EMPTY_VALUE),
    disciplines:
      (issue.disciplines ?? [])
        .map((discipline) => getDisciplineAbbreviation(discipline, language))
        .join(", ") || EMPTY_VALUE,
    startDate: issue.start_date ? formatDateOnly(issue.start_date, language) : EMPTY_VALUE,
    dueDate: issue.due_date ? formatDateOnly(issue.due_date, language) : EMPTY_VALUE,
    // "0", not the dash the other empty cells use: a dash says "we could not
    // resolve this", where a count of none is a fact the reader can rely on.
    // Which is exactly why an issue the comment cap cut short gets the dash
    // instead — what printed is a slice, so its length is not the count.
    commentCount: incompleteComments.has(issue.id) ? EMPTY_VALUE : String(issueComments.length),
    comments: issueComments,
    // Empty for "minutes only" — the caller passes no activity map at all.
    activity: activities.get(issue.id) ?? [],
    attachments: attachmentTable?.get(issue.id) ?? [],
  };
}

/**
 * There is no "deleted" bucket: pm_issues_select is `deleted_at IS NULL AND
 * has_permission(…)`, so a soft-deleted issue is unreadable by the client and
 * the export never receives one. Counting a bucket that can only ever be 0
 * told the reader a deleted issue would show up here, which it never does.
 */
function issueSummary(
  issues: PMIssue[],
  isOpen: (issue: PMIssue) => boolean = (i) => i.status === "in_progress"
): ProtocolDocumentModel["summary"] {
  return issues.reduce(
    (summary, issue) => {
      // Three buckets, but no longer three statuses. A custom status counts as
      // in-progress or closed by its CATEGORY; the old else-branch swept every
      // custom status into "closed", so a protocol reported blocked work done.
      if (isOpen(issue)) summary.inProgress += 1;
      else if (issue.status === "resolved") summary.resolved += 1;
      else summary.closed += 1;
      return summary;
    },
    { inProgress: 0, resolved: 0, closed: 0 }
  );
}

/**
 * The "4 of 12" line, or null when the export covers every issue the period
 * returned. A selection larger than the count it was measured against can only
 * mean the caller passed a stale total, and is treated as complete.
 */
function partialSelection(
  selected: number,
  available: number | undefined,
  copy: Translations["pm"]["issues"]["protocol"]["document"]
): string | null {
  if (available == null || selected >= available) return null;
  return fill(copy.selectionPartial, {
    selected: String(selected),
    total: String(available),
  });
}

/**
 * What the metadata panel says this document contains.
 *
 * Derived, never a stored string: with an arbitrary section set a fixed label
 * would eventually describe a document that is not this one. It is also the
 * only signal distinguishing "no issue table because the author chose that"
 * from "because the export broke".
 */
function documentContents(
  sections: ProtocolSectionSelection,
  hasNotes: boolean,
  hasAttachments: boolean,
  copy: Translations["pm"]["issues"]["protocol"]["document"]
): string {
  const names: string[] = [];
  if (sections.summary) names.push(copy.summary);
  if (sections.issues) names.push(copy.issues);
  if (sections.activity) names.push(copy.activity);
  if (hasNotes) names.push(copy.notes);
  if (hasAttachments) names.push(copy.attachments);
  return names.join(", ");
}

export function buildProtocolDocument({
  data,
  range,
  sections,
  columns,
  notes,
  buildingName,
  buildingProjectId,
  projectName,
  availableIssueCount,
  language,
  translations,
  statusOrder,
  isOpenStatus,
  generatedAt = new Date(),
  documentDate,
  layout = DEFAULT_PROTOCOL_LAYOUT,
  attachments,
}: BuildProtocolDocumentInput): ProtocolDocumentModel {
  const copy = translations.pm.issues.protocol;
  const includeActivity = sections.activity;
  const documentNotes: ProtocolDocumentNote[] = (notes ?? [])
    .filter(isCompleteProtocolNote)
    .map((note) => ({ heading: note.heading.trim(), paragraphs: noteParagraphs(note.body) }));
  // The activity rows are always fetched — they decide which issues belong to
  // the period — but "minutes only" prints none of them, and formatting up to
  // PROTOCOL_ACTIVITY_LIMIT lines just to discard them stalls the export.
  const activities = includeActivity
    ? activityByIssue(data.activity, translations, language)
    : new Map<number, ProtocolDocumentActivity[]>();
  // Only resolved when the column is on: formatting bodies the document will
  // not print is the same waste the activity map already avoids.
  const documentComments =
    sections.issues && columns.commentCount && data.comments
      ? commentsByIssue(data.comments, language, copy.document)
      : new Map<number, ProtocolDocumentComment[]>();
  const issues = sortProtocolIssues(data.issues, statusOrder);
  const documentAttachments = attachments && attachments.files.length > 0 ? attachments : null;

  return {
    language,
    layout,
    title: copy.document.title,
    date: formatProtocolDocumentDate(documentDate, language),
    period: formatProtocolRange(range, language),
    // A header that names the wrong entity is worse than one that admits it
    // does not know: an unresolved building — or a project scope whose name
    // has not loaded — says "Unknown" rather than borrowing another name or
    // claiming the export covers every project.
    building: buildingName || copy.document.unknown,
    project:
      projectName ||
      (buildingProjectId != null ? copy.document.unknown : copy.document.allProjects),
    contents: documentContents(
      sections,
      documentNotes.length > 0,
      documentAttachments !== null,
      copy.document
    ),
    include: sections,
    columns,
    notes: documentNotes,
    attachments: documentAttachments,
    attachmentsInTable: sections.issues && Boolean(documentAttachments?.table),
    generated: dateFormatter(language, true).format(generatedAt),
    // A protocol is filed as the record of a period, so one built from a
    // hand-picked subset must not be byte-identical to the complete record.
    // The fetch's own caveats (truncated / omitted) say the export could not
    // hold everything; this one says the user chose not to.
    selection: partialSelection(issues.length, availableIssueCount, copy.document),
    truncated: Boolean(data.truncated),
    commentsTruncated: Boolean(data.commentsTruncated),
    omitted: Boolean(data.omitted),
    summary: issueSummary(issues, isOpenStatus),
    issues: issues.map((issue) =>
      buildDocumentIssue(
        issue,
        translations,
        activities,
        documentComments,
        data.commentsIncomplete ?? EMPTY_ISSUE_IDS,
        language,
        documentAttachments?.table ?? null
      )
    ),
    labels: copy.document,
  };
}
